import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PreviousIcon from '../../assets/icons/previous.svg';
import NextIcon from '../../assets/icons/next.svg';
import './index.less';

type SelectedDate = {
  day?: number;
  month: number;
  year: number;
};

type DateRange = {
  startDate: SelectedDate | null;
  endDate: SelectedDate | null;
};

type CalendarModalProps = {
  onClose: () => void;
  selectedDate: any;
  onDateSelect?: (startDate: Date, endDate: Date) => void;
};

const monthTranslationKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

const daysOfWeekTranslationKeys = ['sunday_short', 'monday_short', 'tuesday_short', 'wednesday_short', 'thursday_short', 'friday_short', 'saturday_short'];

const DateRangePickerModal: React.FC<CalendarModalProps> = ({ onClose, selectedDate, onDateSelect }) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({ startDate: null, endDate: null });

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number): number => {
    return new Date(year, month, 1).getDay();
  };

  useEffect(() => {
    if (selectedDate) {
      setSelectedDateRange({
        startDate: {
          day: new Date(selectedDate.startDate).getDate(),
          month: new Date(selectedDate.startDate).getMonth(),
          year: new Date(selectedDate.startDate).getFullYear()
        },
        endDate: {
          day: new Date(selectedDate.endDate).getDate(),
          month: new Date(selectedDate.endDate).getMonth(),
          year: new Date(selectedDate.endDate).getFullYear()
        }
      })
    }
  }, [selectedDate])

  const capitalize = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const goToPrevious = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNext = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateSelect = (day?: number, month?: number, year?: number) => {
    const newSelectedDate: SelectedDate = {
      month: month !== undefined ? month : currentDate.getMonth(),
      year: year !== undefined ? year : currentDate.getFullYear(),
    };

    if (day !== undefined) {
      newSelectedDate.day = day;
    }

    if (!selectedDateRange.startDate || selectedDateRange.endDate) {
      setSelectedDateRange({ startDate: newSelectedDate, endDate: null });
    } else {
      const startDate = selectedDateRange.startDate;
      const startTimestamp = new Date(startDate.year, startDate.month, startDate.day || 1).getTime();
      const newTimestamp = new Date(newSelectedDate.year, newSelectedDate.month, newSelectedDate.day || 1).getTime();

      if (newTimestamp < startTimestamp) {
        setSelectedDateRange({ startDate: newSelectedDate, endDate: startDate });
      } else {
        setSelectedDateRange({ ...selectedDateRange, endDate: newSelectedDate });
      }
    }
  };

  const handleFinish = () => {
    if(onDateSelect && selectedDateRange.startDate && selectedDateRange.endDate){
      const startDate = new Date(selectedDateRange.startDate.year, selectedDateRange.startDate.month, selectedDateRange.startDate.day || 1);
      const endDate = new Date(selectedDateRange.endDate.year, selectedDateRange.endDate.month, selectedDateRange.endDate.day || 1);

      onDateSelect(startDate, endDate);
    }

    onClose();
  };

  const renderWeeklyCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
    const firstDayOfMonth = getFirstDayOfMonth(currentDate.getFullYear(), currentDate.getMonth());
    const days = [];

    const dayLabels = (
      <div key="week-header" className="calendar-days-row calendar-header-row">
        {daysOfWeekTranslationKeys.map((day, index) => (
          <div key={`label-${index}`} className="calendar-day-label">
            {capitalize(t(day))}
          </div>
        ))}
      </div>
    );
    days.push(dayLabels);

    let dayCounter = 1;
    const maxWeeks = 6;
    for (let i = 0; i < maxWeeks; i++) {
      const week = [];
      let hasDays = false;
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < firstDayOfMonth) || dayCounter > daysInMonth) {
          week.push(<div key={`empty-${i}-${j}`} className="calendar-day empty"></div>);
        } else {
          const dayNumber = dayCounter;
          const isStartDate =
            selectedDateRange.startDate?.day === dayNumber && selectedDateRange.startDate?.month === currentDate.getMonth() && selectedDateRange.startDate?.year === currentDate.getFullYear();


          const isEndDate = selectedDateRange.endDate?.day === dayNumber && selectedDateRange.endDate?.month === currentDate.getMonth() && selectedDateRange.endDate?.year === currentDate.getFullYear();

          const isInRange =
            selectedDateRange.startDate &&
            selectedDateRange.endDate &&
            new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNumber).getTime() >
            new Date(selectedDateRange.startDate.year, selectedDateRange.startDate.month, selectedDateRange.startDate.day || 1).getTime() &&
            new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNumber).getTime() <
            new Date(selectedDateRange.endDate.year, selectedDateRange.endDate.month, selectedDateRange.endDate.day || 1).getTime();

          const isSelected = isStartDate || isEndDate || isInRange;

          let isDisabled = false;
          if (selectedDateRange?.startDate && !selectedDateRange?.endDate) {
            const startDateTimestamp = new Date(
              selectedDateRange.startDate.year,
              selectedDateRange.startDate.month,
              selectedDateRange.startDate.day || 1
            ).getTime();
            const currentDayTimestamp = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNumber).getTime();
            const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
            isDisabled = currentDayTimestamp > startDateTimestamp + thirtyDaysInMs;
          }

          week.push(
            <div
              key={`day-${i}-${j}`}
              className={`calendar-day ${isSelected ? 'selected' : ''} ${isStartDate ? 'start-date' : ''} ${isEndDate ? 'end-date' : ''} ${isInRange ? 'in-range' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => {
                if (!isDisabled) handleDateSelect(dayNumber);
              }}
            >
              {dayNumber}
            </div>
          );
          dayCounter++;
          hasDays = true;
        }
      }
      if (dayCounter <= daysInMonth || i === 0 || hasDays) {
        days.push(
          <div key={`week-${i}`} className="calendar-days-row">
            {week}
          </div>
        );
      }
    }
    return <div className="calendar-grid">{days}</div>;
  };

  const getCalendarHeaderText = () => {
    return `${capitalize(t(monthTranslationKeys[currentDate.getMonth()]))} ${currentDate.getFullYear()}`;
  };

  return (
    <div className="calendar-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="calendar-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-line-wrapper" onClick={onClose}>
          <div className="modal-header-line"></div>
        </div>
        <div className="calendar-modal-container">
          <div className="calendar-modal-title">{t('filter-by-period')}</div>
          <div className="calendar-modal-description">{t('select-a-period-to-analyze')}</div>
          <div className="calendar-container">
            <div className="calendar-header">
              <img src={PreviousIcon} className="calendar-header-icon" alt="previous" onClick={goToPrevious} />
              <p className="calendar-header-text">{getCalendarHeaderText()}</p>
              <img src={NextIcon} className="calendar-header-icon" alt="next" onClick={goToNext} />
            </div>
            <div className="calendar-date-container">
              {renderWeeklyCalendar()}
            </div>
          </div>
          <div
            className={`calendar-modal-terminate-button ${!selectedDateRange.startDate && !selectedDateRange.endDate ? 'disabled' : ''}`}
            onClick={selectedDateRange.startDate ? handleFinish : undefined}
          >
            {t('finish')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangePickerModal;
